import React, { useState, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SearchBar from '../components/SearchBar'
import UserListItem from '../components/UserListItem'
import SmallBadgeButton from '../components/SmallBadgeButton'
import { userApi } from '../api/user'
import {
  FollowerStatus as FollowerStatusEnum,
  FollowListType,
} from '../constants/follow'
import { FollowerStatus } from '../types/follow'
import { isApp } from '../utils/platform'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'
import LoadingSpinner from '../components/LoadingSpinner'
import Gap from '../components/Gap'
import { pageCache } from '../utils/pageCache'
import { useAuthStore } from '../stores/authStore'

type FollowListItem = {
  id: string
  nickname: string
  username: string
  profile_image: string | null
  status: FollowerStatus
  is_following?: boolean
}

const FollowList: React.FC = () => {
  const navigate = useNavigate()
  const { type: typeParam } = useParams<{ type: string }>()
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId')

  // 경로 파라미터로 분기, 없거나 유효하지 않으면 기본값 followers
  const type =
    typeParam &&
    Object.values(FollowListType).includes(typeParam as FollowListType)
      ? (typeParam as FollowListType)
      : FollowListType.FOLLOWERS

  // 스와이프로 돌아왔을 때 이전 목록을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `follow-list:${type}:${userId ?? 'me'}`
  const [items, setItems] = useState<FollowListItem[]>(
    () => pageCache.get<FollowListItem[]>(cacheKey) ?? [],
  )
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [requestCount, setRequestCount] = useState(0)
  const currentUserId = useAuthStore((s) => s.userId)

  React.useEffect(() => {
    const loadData = async () => {
      try {
        // 이미 그려져 있으면 스피너로 덮지 않는다. 조회는 뒤에서 조용히 끝낸다
        if (!pageCache.has(cacheKey)) {
          setLoading(true)
        }

        if (type === FollowListType.FOLLOWERS) {
          if (userId) {
            // 다른 유저의 팔로워 목록 조회
            const followersResponse = await userApi.getFollowers(userId)
            const acceptedFollowers: FollowListItem[] =
              followersResponse.followers.map((follower) => ({
                ...follower,
                status: follower.status as FollowerStatus,
              }))
            pageCache.set(cacheKey, acceptedFollowers)
            setItems(acceptedFollowers)
          } else {
            // 본인의 팔로워 목록 조회 (수락된 팔로워만)
            const [followersResponse, requestsResponse] = await Promise.all([
              userApi.getFollowers(),
              userApi.getFollowRequests(),
            ])
            const acceptedFollowers: FollowListItem[] =
              followersResponse.followers.map((follower) => ({
                ...follower,
                status: follower.status as FollowerStatus,
              }))

            pageCache.set(cacheKey, acceptedFollowers)
            setItems(acceptedFollowers)
            setRequestCount(requestsResponse.requests.length)
          }
        } else {
          // 팔로잉 목록 조회
          const response = await userApi.getFollowing(userId || undefined)
          const followingItems: FollowListItem[] = response.following.map(
            (row) => ({
              ...row,
              status: row.status as FollowerStatus,
              is_following: row.is_following,
            }),
          )
          pageCache.set(cacheKey, followingItems)
          setItems(followingItems)
        }
      } catch (error) {
        console.error('목록 조회 실패:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [type, userId, cacheKey])

  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) {
      return items
    }

    const searchLower = searchValue.toLowerCase()
    return items.filter(
      (item) =>
        item.nickname.toLowerCase().startsWith(searchLower) ||
        item.username.toLowerCase().startsWith(searchLower),
    )
  }, [items, searchValue])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleCancelFollowing = async (id: string) => {
    try {
      await userApi.cancelFollow(id)
      if (type === FollowListType.FOLLOWERS) {
        // 팔로워 목록에서는 상태만 변경 (none으로 변경)
        setItems((prevItems) =>
          prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: FollowerStatusEnum.NONE,
                  is_following: false,
                }
              : item,
          ),
        )
      } else if (userId) {
        // 타 유저 팔로잉 목록: 행 유지, 조회자 관계만 갱신
        setItems((prevItems) =>
          prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: FollowerStatusEnum.NONE,
                  is_following: false,
                }
              : item,
          ),
        )
      } else {
        // 본인 팔로잉 목록에서는 목록에서 제거
        setItems((prevItems) => prevItems.filter((item) => item.id !== id))
      }
    } catch (error) {
      console.error('팔로우 취소 실패:', error)
    }
  }

  // 내 팔로워 삭제(팔로워 끊기) — 리뷰 삭제와 동일한 확인창 사용
  const handleRemoveFollower = async (id: string) => {
    const ok = await dialogUtil.confirm({
      message: '이 팔로워를 삭제하시겠어요?',
      confirmText: '삭제',
      cancelText: '취소',
    })
    if (!ok) return
    try {
      await userApi.removeFollower(id)
      setItems((prevItems) => prevItems.filter((item) => item.id !== id))
      toastUtil.success('팔로워를 삭제했어요')
    } catch (error) {
      console.error('팔로워 삭제 실패:', error)
      toastUtil.error('삭제 중 오류가 발생했어요')
    }
  }

  const handleRequestFollow = async (id: string) => {
    try {
      const response = await userApi.requestFollow(id)
      const newStatus =
        response.status === 'accepted'
          ? FollowerStatusEnum.ACCEPTED
          : FollowerStatusEnum.PENDING
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === id
            ? {
                ...item,
                status: newStatus,
                is_following: response.status === 'accepted',
              }
            : item,
        ),
      )
    } catch (error) {
      console.error('팔로우 요청 실패:', error)
    }
  }

  const handleFindFriends = () => {
    if (!isApp()) {
      toastUtil.error('앱으로 실행해주세요')
      return
    }
    // 끝나면 이 목록으로 돌아오게 들어온 곳을 알린다(예전엔 온보딩 순위 화면으로 빠졌다)
    navigate('/contact-sync?from=follow')
  }

  // 본인 행은 마이, 그 외는 해당 유저 프로필로 이동한다.
  const handleUserListItemClick = (id: string) => {
    if (currentUserId && id === currentUserId) {
      navigate('/main-my')
      return
    }

    navigate(`/user/${id}/profile`)
  }

  const renderAppBar = () => {
    return (
      <AppBar
        title={`${items.length} ${
          type === FollowListType.FOLLOWERS ? '팔로워' : '팔로잉'
        }`}
        isBottomBorder
      />
    )
  }

  const renderSearchBar = () => {
    return (
      <div className="w-full px-16px py-16px">
        <SearchBar
          placeholder="검색"
          value={searchValue}
          onSearch={handleSearchChange}
        />
      </div>
    )
  }

  const renderLoading = () => {
    return (
      <div className="w-full flex-1 flex flex-col items-center justify-center empty-state-center">
        <LoadingSpinner />
      </div>
    )
  }

  const renderFollowRequests = () => {
    // 마이 페이지가 아니거나 팔로우 요청이 없는 경우 null
    if (userId || requestCount === 0 || type !== FollowListType.FOLLOWERS) {
      return null
    }

    return (
      <div
        className="w-full px-20px pt-8px pb-16px cursor-pointer flex items-center border-b border-472410-20%"
        onClick={() => navigate('/follow-requests')}
      >
        <p className="flex-1 text-style-labelL text-brown-80%-main text-left leading-none">
          팔로우 요청
        </p>
        <Gap width={8} />
        <img
          src="/images/bold-arrow-right-icon.png"
          alt="팔로우 요청"
          className="w-24px h-24px object-contain"
        />
      </div>
    )
  }

  const renderOtherUserFollowButton = (item: FollowListItem) => {
    if (currentUserId && item.id === currentUserId) {
      return null
    }

    const isNoneOrRejected =
      item.status === FollowerStatusEnum.NONE ||
      item.status === FollowerStatusEnum.REJECTED

    const label = isNoneOrRejected
      ? '팔로우'
      : item.status === FollowerStatusEnum.PENDING
        ? '요청중'
        : '팔로잉'

    const variant = isNoneOrRejected ? 'default' : 'inverse'

    const handleClick = () => {
      if (isNoneOrRejected) {
        handleRequestFollow(item.id)
      } else {
        handleCancelFollowing(item.id)
      }
    }

    return (
      <SmallBadgeButton label={label} variant={variant} onClick={handleClick} />
    )
  }

  const renderActionButtons = (item: FollowListItem) => {
    if (userId) {
      return renderOtherUserFollowButton(item)
    }

    if (type === FollowListType.FOLLOWERS) {
      // 내 팔로워 삭제는 별도 버튼 없이 '행 길게 누르기'로(아래 UserListItem onLongPress).
      if (item.status === FollowerStatusEnum.PENDING) {
        return (
          <SmallBadgeButton
            label="요청중"
            variant="inverse"
            onClick={() => handleCancelFollowing(item.id)}
          />
        )
      }

      if (item.status === FollowerStatusEnum.ACCEPTED || item.is_following) {
        return (
          <SmallBadgeButton
            label="팔로잉"
            variant="inverse"
            onClick={() => handleCancelFollowing(item.id)}
          />
        )
      }

      return (
        <SmallBadgeButton
          label="팔로우"
          variant="default"
          onClick={() => handleRequestFollow(item.id)}
        />
      )
    }

    if (item.status === FollowerStatusEnum.PENDING) {
      return (
        <div className="flex items-center">
          <SmallBadgeButton
            label="요청중"
            variant="inverse"
            onClick={() => handleCancelFollowing(item.id)}
          />
        </div>
      )
    }

    return (
      <div className="flex items-center">
        <SmallBadgeButton
          label="팔로잉"
          variant="inverse"
          onClick={() => handleCancelFollowing(item.id)}
        />
      </div>
    )
  }

  const isEmpty = !loading && filteredItems.length === 0

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {/* relative: 빈 안내 문구를 화면 전체 기준 상하·좌우 정가운데로 오버레이 */}
      <div className="app-container h-full flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        {renderAppBar()}

        {/* 검색 바 (항상 유지) */}
        {renderSearchBar()}

        {/* 팔로우 요청 */}
        {renderFollowRequests()}

        {/* 목록 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-16px pb-16px flex flex-col relative">
          {loading ? (
            <>{renderLoading()}</>
          ) : (
            filteredItems.length > 0 && (
              <div className="w-full flex flex-col gap-16px">
                {filteredItems.map((item) => (
                  <UserListItem
                    key={item.id}
                    id={item.id}
                    nickname={item.nickname}
                    username={item.username}
                    profileImage={item.profile_image || undefined}
                    actionButtons={renderActionButtons(item)}
                    onClick={() => handleUserListItemClick(item.id)}
                    onLongPress={
                      !userId && type === FollowListType.FOLLOWERS
                        ? () => handleRemoveFollower(item.id)
                        : undefined
                    }
                  />
                ))}
              </div>
            )
          )}
          {/* 빈 상태: 비어 있는 칸의 광학 중앙(index.css의 .empty-state-center).
              앱바·검색바는 클릭 통과 */}
          {isEmpty && (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
              {searchValue.trim() ? (
                <p className="text-style-body1 text-textColorText3 text-center">
                  검색 결과가 없습니다
                </p>
              ) : !userId ? (
                // 내 목록이 비었을 때: 텍스트를 누르면 연락처 연동(친구 찾기) 페이지로
                <button
                  type="button"
                  onClick={handleFindFriends}
                  className="pointer-events-auto text-style-body1 text-textColorText3 text-center"
                >
                  클릭하고 친구를 찾아보세요
                </button>
              ) : (
                <p className="text-style-body1 text-textColorText3 text-center">
                  {type === FollowListType.FOLLOWERS
                    ? '아직 팔로워가 없습니다'
                    : '아직 팔로잉이 없습니다'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FollowList
